import React, { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Button, Checkbox, Form, Message } from 'semantic-ui-react'
import { FormDropdownField, FormTextFormField } from 'elements'
import { createContact, getAccounts, updateContact } from 'utility/services/crm'
import { toApiError } from 'utility/crm/api-error'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmLookups, useCrmQuery, useCrmToast } from 'hooks/crm'

const NEW_COMPANY = '__new__'

const schema = yup.object().shape({
    Name: yup.string().trim().required('Name is required'),
    Email: yup.string().trim().email('Enter a valid email address').nullable(),
    Company: yup.mixed().test('company', 'Choose a company', value => value !== '' && value !== undefined && value !== null),
    NewAccountName: yup.string().when('Company', {
        is: NEW_COMPANY,
        then: yup.string().trim().required('Company name is required'),
        otherwise: yup.string().nullable(),
    }),
})

/** "Q4, Decision maker" -> ["Q4","Decision maker"], dropping blanks and duplicates. */
export const parseTags = text =>
    Array.from(
        new Set(
            String(text || '')
                .split(',')
                .map(tag => tag.trim())
                .filter(Boolean),
        ),
    )

const FieldError = ({ message }) =>
    message ? (
        <p role="alert" style={{ color: '#dc2626', margin: '2px 0 8px', fontSize: '0.8rem' }}>
            {message}
        </p>
    ) : null

/**
 * New / edit contact. Edit (contact passed) sends Tags as an array; create can also make a new
 * company inline and an "Intro call" follow-up.
 * @param {{
 *   contact?: import('utility/crm/types').CrmContact|null,
 *   defaultAccountId?: number|null,
 *   formId?: string,
 *   showButtons?: boolean,
 *   onSaved: (saved: import('utility/crm/types').CrmContact) => void,
 *   onCancel?: () => void,
 *   onBusyChange?: (busy: boolean) => void,
 * }} props
 */
export const ContactForm = ({
    contact = null,
    defaultAccountId = null,
    formId = 'crm-contact-form',
    showButtons = true,
    onSaved,
    onCancel,
    onBusyChange,
}) => {
    const isEdit = Boolean(contact)
    const { lookups } = useCrmLookups()
    const accounts = useCrmQuery(() => getAccounts({ sort: 'name' }), [], { scopes: ['accounts'] })
    const { successToast } = useCrmToast()
    const [serverError, setServerError] = useState(null)
    const [saving, setSaving] = useState(false)

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            Name: contact?.Name || '',
            Title: contact?.Title || '',
            Email: contact?.Email || '',
            Phone: contact?.Phone || '',
            ContactType: contact?.ContactType || 'Prospect',
            OwnerUserId: contact?.OwnerUserId ?? '',
            TagsText: (contact?.Tags || []).join(', '),
            Company: contact?.AccountId ?? defaultAccountId ?? '',
            NewAccountName: '',
            NewAccountIndustry: '',
            CreateFollowUp: false,
        },
    })
    const company = watch('Company')
    const isNewCompany = company === NEW_COMPANY

    const companyOptions = useMemo(
        () => [
            { key: NEW_COMPANY, value: NEW_COMPANY, text: '+ New company' },
            ...(accounts.data || []).map(a => ({ key: a.AccountId, value: a.AccountId, text: a.Name })),
        ],
        [accounts.data],
    )
    const typeOptions = (lookups?.AccountTypes || ['Prospect', 'Customer', 'Inactive']).map(t => ({ key: t, value: t, text: t }))
    const ownerOptions = [
        { key: 'none', value: '', text: 'Unassigned' },
        ...(lookups?.Users || []).map(u => ({ key: u.UserId, value: u.UserId, text: u.FullName })),
    ]
    const industryOptions = (lookups?.Industries || []).map(i => ({ key: i, value: i, text: i }))

    const submit = async values => {
        setServerError(null)
        setSaving(true)
        onBusyChange?.(true)
        /** @type {import('utility/crm/types').CrmContactInput} */
        const body = {
            Name: values.Name.trim(),
            Title: values.Title?.trim() || undefined,
            Email: values.Email?.trim() || undefined,
            Phone: values.Phone?.trim() || undefined,
            ContactType: values.ContactType || undefined,
            OwnerUserId: values.OwnerUserId === '' ? undefined : Number(values.OwnerUserId),
            Tags: parseTags(values.TagsText),
        }
        if (values.Company === NEW_COMPANY) {
            body.NewAccountName = values.NewAccountName.trim()
            if (values.NewAccountIndustry) body.NewAccountIndustry = values.NewAccountIndustry
        } else {
            body.AccountId = Number(values.Company)
        }
        if (!isEdit && values.CreateFollowUp) body.CreateFollowUp = true
        try {
            const saved = isEdit ? await updateContact(contact.ContactId, body) : await createContact(body)
            successToast(isEdit ? 'Contact updated' : 'Contact created')
            invalidateCrm(...ALL_CRM_SCOPES)
            onSaved(saved)
        } catch (err) {
            // 400/409 messages (DUPLICATE_EMAIL, INVALID_EMAIL, ...) show next to the form.
            setServerError(toApiError(err).message)
        } finally {
            setSaving(false)
            onBusyChange?.(false)
        }
    }

    return (
        <Form id={formId} noValidate onSubmit={handleSubmit(submit)}>
            {serverError ? (
                <Message negative role="alert">
                    {serverError}
                </Message>
            ) : null}

            <FormTextFormField control={control} name="Name" label="Name" required placeholder="Full name" errors={errors.Name} defaultValue="" />
            <FieldError message={errors.Name?.message} />

            <Form.Group widths="equal">
                <FormTextFormField control={control} name="Title" label="Job title" placeholder="e.g. Operations Director" defaultValue="" />
                <FormTextFormField control={control} name="Phone" label="Phone" type="tel" placeholder="Phone" defaultValue="" />
            </Form.Group>

            <FormTextFormField control={control} name="Email" label="Email" type="email" placeholder="name@company.com" errors={errors.Email} defaultValue="" />
            <FieldError message={errors.Email?.message} />

            <FormDropdownField
                control={control}
                name="Company"
                label="Company"
                required
                placeholder="Choose a company"
                options={companyOptions}
                errors={errors.Company}
                defaultValue=""
                loading={accounts.loading}
            />
            <FieldError message={errors.Company?.message} />

            {isNewCompany ? (
                <>
                    <FormTextFormField control={control} name="NewAccountName" label="New company name" required placeholder="Company name" errors={errors.NewAccountName} defaultValue="" />
                    <FieldError message={errors.NewAccountName?.message} />
                    <FormDropdownField
                        control={control}
                        name="NewAccountIndustry"
                        label="Industry"
                        placeholder="Industry (optional)"
                        options={industryOptions}
                        defaultValue=""
                    />
                </>
            ) : null}

            <Form.Group widths="equal">
                <FormDropdownField control={control} name="ContactType" label="Type" options={typeOptions} defaultValue="Prospect" />
                <FormDropdownField control={control} name="OwnerUserId" label="Owner" options={ownerOptions} defaultValue="" />
            </Form.Group>

            <FormTextFormField control={control} name="TagsText" label="Tags" placeholder="Comma separated, e.g. Q4, Decision maker" defaultValue="" />

            {!isEdit ? (
                <Controller
                    control={control}
                    name="CreateFollowUp"
                    render={({ field }) => (
                        <Checkbox
                            label="Create an “Intro call” follow-up for tomorrow"
                            checked={Boolean(field.value)}
                            onChange={(_, d) => field.onChange(Boolean(d.checked))}
                        />
                    )}
                />
            ) : null}

            {showButtons ? (
                <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    {onCancel ? (
                        <Button type="button" onClick={onCancel} disabled={saving}>
                            Cancel
                        </Button>
                    ) : null}
                    <Button type="submit" primary loading={saving} disabled={saving}>
                        {isEdit ? 'Save changes' : 'Create contact'}
                    </Button>
                </div>
            ) : null}
        </Form>
    )
}
